import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { tokenManager } from '@/utils/tokenManager'
import {
  getTableViews,
  resetTableView,
  upsertTableView,
} from '@/services/api/shared/table-view-preferences.service'
import type { TableViewPayload } from '@/types/shared/table-view.types'
import { tableViewScopeCacheKey, useTableViewScope } from '@/hooks/useTableViewScope'

interface TableViewPreferencesContextValue {
  viewsByKey: Record<string, TableViewPayload>
  isLoading: boolean
  scopeKey: string | null
  getView: (viewKey: string) => TableViewPayload | undefined
  saveView: (viewKey: string, payload: TableViewPayload) => Promise<void>
  resetView: (viewKey: string) => Promise<void>
}

const TableViewPreferencesContext = createContext<TableViewPreferencesContextValue | undefined>(
  undefined
)

export function TableViewPreferencesProvider({ children }: { children: ReactNode }) {
  const scope = useTableViewScope()
  const scopeKey = tableViewScopeCacheKey(scope)
  const [viewsByKey, setViewsByKey] = useState<Record<string, TableViewPayload>>({})
  const [isLoading, setIsLoading] = useState(false)
  const saveQueueRef = useRef<Map<string, Promise<void>>>(new Map())

  const fetchViews = useCallback(async () => {
    if (!scope || !scopeKey) {
      setViewsByKey({})
      return
    }
    if (!tokenManager.hasToken('contextToken') && !tokenManager.hasToken('accessToken')) {
      setViewsByKey({})
      return
    }
    setIsLoading(true)
    try {
      const data = await getTableViews(scope)
      setViewsByKey(data.views ?? {})
    } catch {
      setViewsByKey({})
    } finally {
      setIsLoading(false)
    }
  }, [scope, scopeKey])

  useEffect(() => {
    void fetchViews()
  }, [fetchViews])

  const getView = useCallback(
    (viewKey: string) => viewsByKey[viewKey],
    [viewsByKey]
  )

  const saveView = useCallback(
    async (viewKey: string, payload: TableViewPayload) => {
      if (!scope) return
      setViewsByKey((prev) => ({ ...prev, [viewKey]: payload }))
      const run = async () => {
        try {
          await upsertTableView(scope, viewKey, payload)
        } catch {
          /* keep optimistic local state */
        }
      }
      const pending = saveQueueRef.current.get(viewKey)
      const next = pending ? pending.then(run, run) : run()
      saveQueueRef.current.set(viewKey, next)
      await next
      saveQueueRef.current.delete(viewKey)
    },
    [scope]
  )

  const resetViewFn = useCallback(
    async (viewKey: string) => {
      if (!scope) return
      await resetTableView(scope, viewKey)
      setViewsByKey((prev) => {
        const next = { ...prev }
        delete next[viewKey]
        return next
      })
    },
    [scope]
  )

  const value = useMemo(
    (): TableViewPreferencesContextValue => ({
      viewsByKey,
      isLoading,
      scopeKey,
      getView,
      saveView,
      resetView: resetViewFn,
    }),
    [viewsByKey, isLoading, scopeKey, getView, saveView, resetViewFn]
  )

  return (
    <TableViewPreferencesContext.Provider value={value}>
      {children}
    </TableViewPreferencesContext.Provider>
  )
}

export function useTableViewPreferences(): TableViewPreferencesContextValue {
  const ctx = useContext(TableViewPreferencesContext)
  if (!ctx) {
    throw new Error('useTableViewPreferences must be used within TableViewPreferencesProvider')
  }
  return ctx
}
